import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Image, CartPackage } from '../types';
import { useAuth } from './AuthContext';
import { usePrices } from './PriceContext';
import { cartService } from '../services/cartService';
import { analytics } from '../services/analytics';

interface CartContextType {
  cartItems: Image[];
  cartPackages: CartPackage[];
  totalPrice: number;
  addToCart: (image: Image) => Promise<void>;
  removeFromCart: (imageId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  isInCart: (imageId: string) => boolean;
  isLoading: boolean;
  preloadCart: (userId: number, googleId: string) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<Image[]>([]);
  const [cartPackages, setCartPackages] = useState<CartPackage[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { user, registerPreloadFunction } = useAuth();
  const { getPriceByType } = usePrices();

  // Cache inteligente + precarga
  const loadCart = async (userId: number, googleId: string, forceRefresh = false) => {
    // PASO 1: Cargar INMEDIATAMENTE desde localStorage (rápido)
    if (!forceRefresh) {
      const storedCart = localStorage.getItem(`cart_${googleId}`);
      if (storedCart) {
        setCartItems(JSON.parse(storedCart));
      }
    }

    // PASO 2: Actualizar desde backend en background
    try {
      const backendCart = await cartService.getCart(userId);
      setCartItems(backendCart);
      // Actualizar cache para próxima vez
      localStorage.setItem(`cart_${googleId}`, JSON.stringify(backendCart));
    } catch (error) {
      console.error('Error loading cart from backend:', error);
      // Si falla y no hay cache, mantener array vacío
      if (!localStorage.getItem(`cart_${googleId}`)) {
        setCartItems([]);
      }
    }
  };

  useEffect(() => {
    const initializeCart = async () => {
      if (user?.userId) {
        setIsLoading(true);
        await loadCart(user.userId, user.id);
        setIsLoading(false);
      } else if (user) {
        // Solo localStorage si no hay backend ID
        const storedCart = localStorage.getItem(`cart_${user.id}`);
        if (storedCart) {
          setCartItems(JSON.parse(storedCart));
        }
      } else {
        setCartItems([]);
      }
    };
    
    initializeCart();
  }, [user]);

  useEffect(() => {
    // Organize packages when cart items change
    organizePackages();
  }, [cartItems]);

  const saveCart = (updatedCart: Image[]) => {
    if (user) {
      localStorage.setItem(`cart_${user.id}`, JSON.stringify(updatedCart));
    }
  };

  const addToCart = async (image: Image) => {
    if (!isInCart(image.id) && user) {
      try {
        if (user.userId) {
          await cartService.addToCart({
            idCliente: user.userId,
            idImagen: parseInt(image.id),
            secuencia: cartItems.length + 1
          });
        }
        
        const updatedCart = [...cartItems, image];
        setCartItems(updatedCart);
        saveCart(updatedCart);
        
        // Track add to cart
        analytics.addToCart(image.id, image.price);
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }
  };

  const removeFromCart = async (imageId: string) => {
    if (user) {
      try {
        if (user.userId) {
          await cartService.removeFromCart(user.userId, parseInt(imageId));
        }
        
        const updatedCart = cartItems.filter(img => img.id !== imageId);
        setCartItems(updatedCart);
        saveCart(updatedCart);
        
        // Track remove from cart
        analytics.removeFromCart(imageId);
      } catch (error) {
        console.error('Error removing from cart:', error);
      }
    }
  };

  const clearCart = async () => {
    if (user) {
      try {
        if (user.userId) {
          await cartService.clearCart(user.userId);
        }
        
        setCartItems([]);
        saveCart([]);
        
        // Track clear cart
        analytics.clearCart();
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    }
  };

  const isInCart = (imageId: string) => {
    return cartItems.some(img => img.id === imageId);
  };

  const organizePackages = () => {
    // Create package groups based on requirements
    const newPackages: CartPackage[] = [];
    let remainingItems = [...cartItems];
    let newTotalPrice = 0;

    // Create packages of 10
    while (remainingItems.length >= 10) {
      const pack10Images = remainingItems.slice(0, 10);
      // Obtener precio del contexto de precios
      const packPrice = pack10Images.length * getPriceByType('pack10');

      newPackages.push({
        type: 'pack10',
        images: pack10Images,
        price: packPrice
      });

      newTotalPrice += packPrice;
      remainingItems = remainingItems.slice(10);
    }

    // Create packages of 5
    while (remainingItems.length >= 5) {
      const pack5Images = remainingItems.slice(0, 5);
      // Obtener precio del contexto de precios
      const packPrice = pack5Images.length * getPriceByType('pack5');

      newPackages.push({
        type: 'pack5',
        images: pack5Images,
        price: packPrice
      });

      newTotalPrice += packPrice;
      remainingItems = remainingItems.slice(5);
    }

    // Remaining individual images
    remainingItems.forEach(image => {
      // Obtener precio del contexto de precios
      const price = getPriceByType('individual');
      
      newPackages.push({
        type: 'individual',
        images: [image],
        price: price
      });
      newTotalPrice += price;
    });

    setCartPackages(newPackages);
    setTotalPrice(newTotalPrice);
  };

  // Exponer función de precarga para AuthContext
  const preloadCart = async (userId: number, googleId: string) => {
    await loadCart(userId, googleId);
  };

  // Registrar función de precarga en AuthContext
  React.useEffect(() => {
    if (registerPreloadFunction) {
      registerPreloadFunction('cart', preloadCart);
    }
  }, [registerPreloadFunction]);

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      cartPackages, 
      totalPrice,
      addToCart, 
      removeFromCart, 
      clearCart, 
      isInCart,
      isLoading,
      preloadCart
    }}>
      {children}
    </CartContext.Provider>
  );
};